import axios from 'axios';

export const normalizeApiBaseUrl = (url: string): string => url.replace(/\/api\/?$/, '');

const resolveApiBaseUrl = (): string => {
  const configured = (import.meta as any).env?.VITE_API_URL as string | undefined;
  if (configured?.trim()) {
    return normalizeApiBaseUrl(configured.trim());
  }
  if (window.location.hostname === 'localhost') {
    return 'http://localhost:5000';
  }
  return 'https://poverkaagent.ru';
};

const API_BASE_URL = resolveApiBaseUrl();

// Создаем axios инстанс с базовым URL
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Добавляем interceptor для автоматического добавления токена
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Добавляем interceptor для обработки ошибок авторизации
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Только если мы НЕ на странице логина
      if (window.location.pathname !== '/login') {
        localStorage.removeItem('access_token');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// API методы для CRM
export const crmApi = {
  // Получить сделки с фильтрами
  getDeals: async (filters: any = {}) => {
    const response = await api.get('/api/v1/crm/deals', { params: filters });
    return response.data;
  },

  // Получить статистику по сделкам
  getDealsStats: async (filters: any = {}) => {
    const response = await api.get('/api/v1/crm/deals/stats', { params: filters });
    return response.data;
  },
};

// Интерфейс для фильтров клиента
interface ClientDealsFilters {
  month?: number;
  year?: number;
  stageId?: string;
  dateType?: 'created' | 'closed';
}

// API методы для клиентов
export const clientApi = {
  // Получить сделки клиента
  getClientDeals: async (filters: ClientDealsFilters = {}) => {
    const response = await api.get('/api/v1/client/deals', { params: filters });
    return response.data;
  },

  // Получить статистику клиента
  getClientStats: async (filters: ClientDealsFilters) => {
    const response = await api.get('/api/v1/client/stats', { params: filters });
    return response.data;
  },

  // Получить профиль клиента
  getClientProfile: async () => {
    const response = await api.get('/api/v1/client/profile');
    return response.data;
  },

  // Экспорт сделок клиента в Excel
  exportClientDealsToExcel: async (filters: ClientDealsFilters = {}) => {
    const response = await api.get('/api/v1/client/export-excel', { 
      params: filters,
      responseType: 'blob'
    });
    return response.data;
  },

  exportClientDealsToTPlusReport: async (filters: ClientDealsFilters = {}) => {
    const response = await api.get('/api/v1/client/export-tplus-report', {
      params: filters,
      responseType: 'blob'
    });
    return response.data;
  },

  // Отправить заявку в CRM
  submitDeal: async (dealData: { name: string; phone: string; street: string; house: string; apartment?: string; comment?: string }) => {
    const response = await api.post('/api/v1/client/submit-deal', dealData);
    return response.data;
  },

  // Обновить настройку отключения вознаграждений
  updateDisableRewards: async (disableRewards: boolean) => {
    const response = await api.post('/api/v1/client/disable-rewards', { disableRewards });
    return response.data;
  },
};

// Интерфейс для фильтров менеджера
interface ManagerDealsFilters {
  month?: number;
  year?: number;
  stageId?: string;
  dateType?: 'created' | 'closed';
  ukNames?: string[];
  sourceIds?: string[];
}

// API методы для менеджеров
export const managerApi = {
  // Получить сделки менеджера
  getManagerDeals: async (filters: ManagerDealsFilters = {}) => {
    const response = await api.get('/api/v1/manager/deals', { params: filters });
    return response.data;
  },

  // Получить статистику менеджера
  getManagerStats: async (filters: ManagerDealsFilters = {}) => {
    const response = await api.get('/api/v1/manager/stats', { params: filters });
    return response.data;
  },

  // Получить профиль менеджера
  getManagerProfile: async () => {
    const response = await api.get('/api/v1/manager/profile');
    return response.data;
  },

  // Получить доступные источники
  getManagerSources: async () => {
    const response = await api.get('/api/v1/manager/sources');
    return response.data;
  },

  // Получить доступные УК
  getManagerUKs: async () => {
    const response = await api.get('/api/v1/manager/uks');
    return response.data;
  },

  // Экспорт сделок менеджера в Excel
  exportManagerDealsToExcel: async (filters: ManagerDealsFilters = {}) => {
    const response = await api.get('/api/v1/manager/export-excel', { 
      params: filters,
      responseType: 'blob'
    });
    return response.data;
  },

  exportManagerDealsToTPlusReport: async (filters: ManagerDealsFilters = {}) => {
    const response = await api.get('/api/v1/manager/export-tplus-report', {
      params: filters,
      responseType: 'blob'
    });
    return response.data;
  },

  // Обновить настройку отключения вознаграждений
  updateDisableRewards: async (disableRewards: boolean) => {
    const response = await api.post('/api/v1/manager/disable-rewards', { disableRewards });
    return response.data;
  },
};

// API методы для аутентификации
export const authApi = {
  login: async (credentials: { login: string; password: string }) => {
    const response = await api.post('/api/v1/auth/login', credentials);
    return response.data;
  },

  getProfile: async () => {
    const response = await api.get('/api/v1/auth/profile');
    return response.data;
  },
};

// API методы для админ-панели
export const adminApi = {
  // Получить всех агентов
  getAgents: async () => {
    const response = await api.get('/api/v1/admin/agents');
    return response.data;
  },

  // Создать агента
  createAgent: async (agentData: any) => {
    const response = await api.post('/api/v1/admin/agents', agentData);
    return response.data;
  },

  // Обновить агента
  updateAgent: async (id: string, agentData: any) => {
    const response = await api.put(`/api/v1/admin/agents/${id}`, agentData);
    return response.data;
  },

  // Удалить агента
  deleteAgent: async (id: string) => {
    const response = await api.delete(`/api/v1/admin/agents/${id}`);
    return response.data;
  },

  // Получить все источники
  getSources: async () => {
    const response = await api.get('/api/v1/admin/sources');
    return response.data;
  },

  // Создать источник
  createSource: async (sourceData: any) => {
    const response = await api.post('/api/v1/admin/sources', sourceData);
    return response.data;
  },

  // Обновить источник
  updateSource: async (id: string, sourceData: any) => {
    const response = await api.put(`/api/v1/admin/sources/${id}`, sourceData);
    return response.data;
  },

  // Удалить источник
  deleteSource: async (id: string) => {
    const response = await api.delete(`/api/v1/admin/sources/${id}`);
    return response.data;
  },

  // Получить все тарифы
  getTariffs: async () => {
    const response = await api.get('/api/v1/admin/tariffs');
    return response.data;
  },

  // Создать тариф
  createTariff: async (tariffData: any) => {
    const response = await api.post('/api/v1/admin/tariffs', tariffData);
    return response.data;
  },

  // Обновить тариф
  updateTariff: async (id: string, tariffData: any) => {
    const response = await api.put(`/api/v1/admin/tariffs/${id}`, tariffData);
    return response.data;
  },

  // Удалить тариф
  deleteTariff: async (id: string) => {
    const response = await api.delete(`/api/v1/admin/tariffs/${id}`);
    return response.data;
  },

  // Получить все продукты
  getProducts: async () => {
    const response = await api.get('/api/v1/admin/products');
    return response.data;
  },

  // Создать продукт
  createProduct: async (productData: any) => {
    const response = await api.post('/api/v1/admin/products', productData);
    return response.data;
  },

  // Обновить продукт
  updateProduct: async (id: string, productData: any) => {
    const response = await api.put(`/api/v1/admin/products/${id}`, productData);
    return response.data;
  },

  // Удалить продукт
  deleteProduct: async (id: string) => {
    const response = await api.delete(`/api/v1/admin/products/${id}`);
    return response.data;
  },

  // Получить все УК
  getUKs: async () => {
    const response = await api.get('/api/v1/admin/uks');
    return response.data;
  },

  // Создать УК
  createUK: async (ukData: any) => {
    const response = await api.post('/api/v1/admin/uks', ukData);
    return response.data;
  },

  // Обновить УК
  updateUK: async (id: string, ukData: any) => {
    const response = await api.put(`/api/v1/admin/uks/${id}`, ukData);
    return response.data;
  },

  // Удалить УК
  deleteUK: async (id: string) => {
    const response = await api.delete(`/api/v1/admin/uks/${id}`);
    return response.data;
  },
};

// Публичные API методы для товаров (доступно всем авторизованным пользователям)
export const productsApi = {
  // Получить список активных товаров
  getProducts: async () => {
    const response = await api.get('/api/v1/products');
    return response.data;
  },
};

export default api;
